import type { EnumItem, EnumsResponse } from '../api/adminServices'
import type { Language } from '../store/settingsStore'

const ALL_LANGUAGES: Language[] = ['EN', 'HE', 'AR', 'RU']

function labelFromMap(map: Record<string, string>, language: Language): string | null {
  const direct = map[language] ?? map[language.toLowerCase()] ?? map[language.toUpperCase()]
  return direct?.trim() ? direct.trim() : null
}

/** Resolve display label for one enum item and UI language. */
export function resolveEnumItemLabel(item: EnumItem, language: Language): string | null {
  const code = String(item.code ?? '').trim()
  if (!code) return null

  if (item.labels && typeof item.labels === 'object') {
    const fromLabels = labelFromMap(item.labels as Record<string, string>, language)
    if (fromLabels) return fromLabels
  }

  if (typeof item.label === 'string' && item.label.trim()) {
    return item.label.trim()
  }

  if (item.label && typeof item.label === 'object') {
    const fromLabel = labelFromMap(item.label as Record<string, string>, language)
    if (fromLabel) return fromLabel
  }

  return null
}

function putInMap(
  map: Record<string, string>,
  enumName: string,
  code: string,
  label: string,
): void {
  map[`${enumName}.${code}`] = label
  map[`${enumName}.${code.toUpperCase()}`] = label
}

/** Build per-language lookup maps from a metadata/enums payload. */
export function buildEnumMaps(
  enums: EnumsResponse,
  singleLocale?: Language,
): Partial<Record<Language, Record<string, string>>> {
  const maps: Partial<Record<Language, Record<string, string>>> = {}

  for (const [enumName, items] of Object.entries(enums)) {
    if (!Array.isArray(items)) continue

    for (const raw of items) {
      if (!raw || typeof raw !== 'object') continue
      const item = raw as EnumItem
      const code = String(item.code ?? '').trim()
      if (!code) continue

      const hasMultiLocale =
        (item.labels && typeof item.labels === 'object' && Object.keys(item.labels).length > 0) ||
        (item.label && typeof item.label === 'object')

      if (hasMultiLocale) {
        for (const lang of ALL_LANGUAGES) {
          const label = resolveEnumItemLabel(item, lang)
          if (!label) continue
          if (!maps[lang]) maps[lang] = {}
          putInMap(maps[lang]!, enumName, code, label)
        }
      } else if (singleLocale) {
        const label = resolveEnumItemLabel(item, singleLocale) ?? code
        if (!maps[singleLocale]) maps[singleLocale] = {}
        putInMap(maps[singleLocale]!, enumName, code, label)
      }
    }
  }

  return maps
}

export function lookupEnumLabel(
  map: Record<string, string> | undefined,
  enumName: string,
  code: string,
): string | null {
  if (!map || !code) return null

  const normalizedCode = String(code).trim()
  const normalizedEnumName = enumName.trim()
  const upperCode = normalizedCode.toUpperCase()

  for (const key of [
    `${normalizedEnumName}.${normalizedCode}`,
    `${normalizedEnumName}.${upperCode}`,
  ]) {
    if (map[key]) return map[key]
  }

  for (const [key, value] of Object.entries(map)) {
    if (!key.includes('.')) continue
    const [keyEnumName, keyCode] = key.split('.', 2)
    if (
      keyEnumName.toLowerCase() === normalizedEnumName.toLowerCase() &&
      (keyCode === normalizedCode || keyCode.toUpperCase() === upperCode)
    ) {
      return value
    }
  }

  return map[normalizedCode] ?? map[upperCode] ?? null
}

export function hasEnumMapsForLanguage(
  maps: Partial<Record<Language, Record<string, string>>>,
  language: Language,
): boolean {
  const map = maps[language]
  return !!map && Object.keys(map).length > 0
}
